import * as React from 'react';
import { cn, getTestId, type TestIdProps } from '../../utils';
import { useTranslation } from '../../providers';
import { ErrorIllustrationMap, ErrorIllustrationName } from '../generated/svg';

/**
 * Top padding per variant — controls where the title (and any action below it)
 * vertically lands inside the box. Tuned so the title visually lines up with each
 * illustration's focal point: the digits for 500/404, and the belly for genericError.
 * If you re-crop an illustration's viewBox, retune the matching entry here.
 */
const VARIANT_TITLE_TOP: Record<ErrorIllustrationName, string> = {
  'error-server': 'pt-60',
  'error-not-found': 'pt-64',
  'error-generic': 'pt-64'
};

type BaseAttrs = Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

export interface ErrorStateProps extends BaseAttrs, TestIdProps {
  /** Which built-in scenario to render. */
  variant: ErrorIllustrationName;
  /** Override the variant's default title. */
  title?: string;
  /** Optional supporting line under the title — no default copy ships per variant. */
  subtitle?: string;
  /**
   * Override the variant's default background illustration. Pass any `ReactNode`
   * (typically an imported `<svg>` component); use `null` to hide the illustration entirely.
   */
  illustration?: React.ReactNode;
  /** Optional action element, usually a "Try again" / "Go home" button. */
  action?: React.ReactNode;
  id?: string;
}

/**
 * ErrorState — full-block "something went wrong" surface.
 *
 * Each variant ships a default background illustration (a watermark SVG) with the title
 * overlaid on top. Pick a `variant` for built-in copy + artwork.
 *
 * @example
 * ```tsx
 * <ErrorState variant='error-server' />
 * <ErrorState variant='error-not-found' />
 * <ErrorState
 *   variant='error-generic'
 *   illustration={<MyCustomMascot />}
 *   action={<Button onClick={retry}>Try again</Button>}
 * />
 * ```
 */
export const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(
  ({ variant, title, subtitle, illustration, action, className, testId, id, ...props }, ref) => {
    const t = useTranslation();

    const titleKey = `errorState.${variant}.title`;
    const subtitleKey = `errorState.${variant}.subtitle`;
    const finalTitle = title ?? t(titleKey);
    const finalSubtitle = subtitle ?? t(subtitleKey);
    const DefaultIllustration = ErrorIllustrationMap[variant];
    const renderedIllustration =
      illustration === undefined ? <DefaultIllustration className='w-full h-full' /> : illustration;
    return (
      <div
        ref={ref}
        role='alert'
        data-testid={getTestId({ testId, id })}
        className={cn(
          'relative flex flex-col items-center text-center text-foreground-base',
          'min-h-96 pb-xl px-md',
          VARIANT_TITLE_TOP[variant] ?? VARIANT_TITLE_TOP['error-generic'],
          className
        )}
        {...props}
      >
        {renderedIllustration && (
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-0 flex items-center justify-center text-foreground-muted opacity-60'
          >
            {renderedIllustration}
          </div>
        )}
        <div className='relative z-10 flex flex-col items-center gap-md'>
          <div className='flex flex-col items-center gap-xs'>
            <h2 className='text-2xl font-semibold'>{finalTitle}</h2>
            {finalSubtitle && (
              <p className='text-md text-foreground-muted max-w-md'>{finalSubtitle}</p>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      </div>
    );
  }
);

ErrorState.displayName = 'ErrorState';
